import { ReloadOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Pagination,
  Popover,
  Row,
  Select,
  Table,
} from "antd";
import Dropdown from "antd/es/dropdown/dropdown";
import { debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { FaSquareFull } from "react-icons/fa";
import { FiMoreHorizontal } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiFactory from "../../../api";
import { PROJECT } from "../../../config/Constant";
import { useWorkManagementContext } from "../../../context/WorkManagementContext";
import { useInfoUser } from "../../../store/UserStore";
import { formatDate } from "../../../utils/formatTime";
import "../../../styles/table.scss";
import {openNotificationError} from "../../../utils/Utils";

const ProjectList = ({ isMobile, backBtn }) => {
  const { user, languageMap } = useInfoUser();
  const navigate = useNavigate();
  const { projectTypeOptions, progressStepOptions } =
    useWorkManagementContext();
  const [projectList, setProjectList] = useState([]);
  const [projectSearch, setProjectSearch] = useState({
    projectName: "",
    projectType: "All",
  });
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: PROJECT.PROJECT_LIMIT,
    total: 0,
    current: 1,
  });
  const [isEnableCreateProject, setIsEnableCreateProject] = useState(false);
  const [isEnableCreateProjectPopover, setIsEnableCreateProjectPopover] =
    useState(false);
  const listRef = useRef(null);
  const renderDropdownMenu = (record) => {
    const dropdownMenuItems = [
      {
        key: "2",
        label: (
          <div
            onClick={() => handleCalendarClick(record)}
            className="w-[150px]"
          >
            {languageMap?.["event.dashboard.myProject.calendar"] ?? "Calendar"}
          </div>
        ),
      },
      isEnableCreateProject
        ? {
            key: "1",
            label: (
              <div
                onClick={() => handleProjectNameClickProjectDetail(record)}
                className="w-[150px]"
              >
                {languageMap?.[
                  "project?.management.config.authority.project_manager_btn"
                ] ?? "Project Management"}
              </div>
            ),
          }
        : null,
    ];

    return (
      <Dropdown
        menu={{
          items: dropdownMenuItems,
        }}
        placement="bottom"
        arrow
        trigger={"click"}
      >
        <FiMoreHorizontal className="cursor-pointer" size={20} />
      </Dropdown>
    );
  };

  const fetchProjectsData = async (newPagination = pagination) => {
    setLoading(true);
    try {
      const { current, pageSize } = newPagination;
      const request = {
        limit: pageSize,
        page: current,
        search: projectSearch,
      };

      const result = await apiFactory.projectApi.getList(request);
      if (result?.status === 200) {
        setProjectList(result?.data?.items || []);
        setPagination((prev) => ({
          ...prev,
          total: result?.data?.totalItems || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectsData();
  }, [pagination.current, pagination.pageSize, projectSearch]);

  const handleTableChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleSearch = (value) => {
    setProjectSearch((prevState) => ({
      ...prevState,
      projectName: value,
    }));
    setPagination({ current: 1, pageSize: 10, total: 0 });
  };

  const debouncedHandleSearch = debounce((value) => {
    setProjectSearch((prevState) => ({
      ...prevState,
      projectName: value,
    }));
    setPagination({ current: 1, pageSize: 10, total: 0 });
  }, 500);

  const onSearchChange = (event) => {
    setSearchText(event?.target?.value);
    debouncedHandleSearch(event?.target?.value);
  };

  const handleSelectChange = (value) => {
    setProjectSearch({ projectType: value });
    setPagination({ current: 1, pageSize: 10, total: 0 });
  };

  const handleReload = () => {
    setProjectSearch({ projectName: "", projectType: "All" });
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setSearchText("");
  };

  const handleTimelineClick = (record) => {
    navigate(`/work-management/timeline/${record.projectCode}`);
  };

  const handleCalendarClick = (record) => {
    navigate(`/work-management/calendar/${record.projectCode}`);
  };
  const handleProjectNameClickProjectDetail = (record) => {
    navigate(`/work-management/project/${record.projectCode}`);
  };

  const handleChangeStar = async (record) => {
    const result = await apiFactory.projectApi.changeProjectBookmark(
      record?.projectCode
    );
    if (result?.status !== 200) {
      return openNotificationError(result?.message);
    }
    record.projectBookmarkDto.status = result?.data?.status;

    const projects = projectList.map((item) => {
      if (item.projectCode === record.projectCode) {
        item.projectBookmarkDto.status = record.projectBookmarkDto.status;
        return item;
      }
      return item;
    });
    setProjectList(projects);
  };

  const columns = [
    {
      align: "center",
      title: "",
      dataIndex: "color",
      key: "color",
      render: (color) => (
        <FaSquareFull size={20} color={color} className="rounded-[2px]" />
      ),
      width: 40,
    },
    {
      title: `${languageMap?.["event.dashboard.myProject.projectName"] ?? "Project Name"}`,
      dataIndex: "name",
      key: "name",
      width: 400,
      render: (text, record) => (
        <div
          className="cursor-pointer h-full w-full flex items-center"
          onClick={() => handleTimelineClick(record)}
        >
          {text}
        </div>
      ),
    },
    {
      title: `${languageMap?.["event.dashboard.myProject.startDate"] ?? "Start Date"}`,
      dataIndex: "startDate",
      key: "startDate",
      width: 150,
      render: (date) => <span>{formatDate(date)}</span>,
    },
    {
      title: `${languageMap?.["event.dashboard.myProject.endDate"] ?? "End Date"}`,
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: (date) => <span>{formatDate(date)}</span>,
    },
    {
      title: `${languageMap?.["event.dashboard.myProject.status"] ?? "Status"}`,
      dataIndex: "progressStepCode",
      width: 150,
      key: "progressStepCode",
      render: (_, record) => (
        <span>
          {
            progressStepOptions.find(
              (item) => item.code === record.progressStepCode
            )?.[{ us: "enName", vn: "vnName", kr: "krName" }[user?.language]]
          }
        </span>
      ),
    },
    {
      title: `${languageMap?.["event.dashboard.myProject.type"] ?? "Type"}`,
      dataIndex: "segmentCode",
      key: "segmentCode",
      width: 200,
      render: (_, record) => (
        <span>
          {
            projectTypeOptions.find(
              (item) => item.code === record.segmentCode
            )?.[{ us: "enName", vn: "vnName", kr: "krName" }[user?.language]]
          }
        </span>
      ),
    },
    {
      title: `${languageMap?.["event.dashboard.myProject.createdDate"] ?? "Created Date"}`,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => formatDate(date),
    },
    {
      align: "center",
      title: `${languageMap?.["event.dashboard.myProject.action"] ?? "Hành động"}`,
      key: "action",
      render: (_, record) => (
        <button onClick={() => handleChangeStar(record)}>
          {record.projectBookmarkDto?.status === "Y" ? (
            <StarFilled style={{ fontSize: "25px", color: "#FFCC00" }} />
          ) : (
            <StarOutlined style={{ fontSize: "25px" }} />
          )}
        </button>
      ),
      width: 74,
    },
    {
      align: "center",
      key: "more",
      fixed: "right",
      render: (_, record) => <span>{renderDropdownMenu(record)}</span>,
      width: 40,
    },
  ];

  const checkOrgUser = async () => {
    try {
      const result = await apiFactory.organizationUserApi.existOrgUser();

      if (result?.status !== 200) {
        return;
      }

      setIsEnableCreateProject(result?.data);
    } catch (error) {}
  };

  const handleOpenChange = (newOpen) => {
    setIsEnableCreateProjectPopover(newOpen);
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: "instant", block: "start" });
    }
  };

  useEffect(() => {
    checkOrgUser();
  }, []);

  return (
    <>
      <div
        ref={listRef}
        className={`my-project ${isMobile ? "fixed-header" : ""}`}
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div className="flex justify-between gap-[10px]">
          {backBtn}
          <div className="font-semibold">
            {languageMap?.["event.dashboard.myProject.title"] ?? "My Project"}
          </div>
          <ReloadOutlined
            className="text-[20px] reload"
            onClick={handleReload}
          />
        </div>
        <div className="search-condition flex flex-col md:flex-row gap-[10px]">
          <Input
            className="search-project w-full"
            value={searchText}
            onChange={onSearchChange}
            onSearch={handleSearch}
            placeholder={
              languageMap?.["event.dashboard.myProject.searchProject"] ??
              "Search project name"
            }
          />

          <div className="flex gap-[10px] w-full md:w-auto">
            <Select
              className="select-dropdown w-full md:w-auto min-w-[150px]"
              value={
                languageMap?.[
                  `event.dashboard.myProject.${projectSearch?.projectType.toLowerCase()}`
                ] ?? `${projectSearch?.projectType}`
              }
              onChange={handleSelectChange}
              options={[
                {
                  label:
                    languageMap?.["event.dashboard.myProject.all"] ?? "All",
                  value: "ALL",
                },
                {
                  label:
                    languageMap?.["event.dashboard.myProject.favorites"] ??
                    "Favorites",
                  value: "FAVORITES",
                },
                {
                  label:
                    languageMap?.["event.dashboard.myProject.recently"] ??
                    "Recently",
                  value: "RECENTLY",
                },
              ]}
            />

            <Popover
              content={
                languageMap?.[
                  "event.dashboard.myProject.addProject?.isDisabled"
                ] ?? "You need have org"
              }
              trigger="hover"
              open={!isEnableCreateProject && isEnableCreateProjectPopover}
              onOpenChange={handleOpenChange}
            >
              <Button
                className="w-full md:w-auto min-w-[150px]"
                onClick={() => navigate("/work-management/project")}
                disabled={!isEnableCreateProject}
                type="primary"
              >
                {languageMap?.["event.dashboard.myProject.addProject"] ??
                  "Add more project"}
              </Button>
            </Popover>
          </div>
        </div>
      </div>

      <div style={{ marginTop: isMobile ? "120px" : "" }}>
        {" "}
        {isMobile ? (
          <Row gutter={[16, 16]}>
            {projectList?.map((project) => (
              <Col
                key={project?.projectCode}
                xs={24}
                sm={12}
                md={8}
                lg={6}
                xl={4}
                className=""
              >
                <Card
                  title={
                    <div className="flex items-center gap-2">
                      <FaSquareFull size={16} color={project?.color} />
                      <span
                        className="whitespace-pre-wrap"
                        style={{ color: project?.color }}
                      >
                        {project?.name}
                      </span>
                    </div>
                  }
                  bordered={false}
                  className="shadow-md rounded"
                  style={{ border: `1px solid #cccccc5e` }}
                  extra={
                    <Button
                      type="text"
                      onClick={() => handleChangeStar(project)}
                      icon={
                        project?.projectBookmarkDto?.status === "Y" ? (
                          <StarFilled style={{ color: "#FFCC00" }} />
                        ) : (
                          <StarOutlined />
                        )
                      }
                    />
                  }
                >
                  <div className="p-[10px]">
                    <p>
                      <b>
                        {languageMap?.["event.dashboard.myProject.startDate"] ??
                          "Start Date"}
                        :
                      </b>{" "}
                      {formatDate(project?.startDate)}
                    </p>
                    <p>
                      <b>
                        {languageMap?.["event.dashboard.myProject.endDate"] ??
                          "End Date"}
                        :
                      </b>{" "}
                      {formatDate(project?.endDate)}
                    </p>
                    <p>
                      <b>
                        {languageMap?.["event.dashboard.myProject.status"] ??
                          "Status"}
                        :
                      </b>{" "}
                      {
                        progressStepOptions.find(
                          (item) => item.code === project?.progressStepCode
                        )?.[
                          { us: "enName", vn: "vnName", kr: "krName" }[
                            user?.language
                          ]
                        ]
                      }
                    </p>
                    <p>
                      <b>
                        {languageMap?.["event.dashboard.myProject.type"] ??
                          "Type"}
                        :
                      </b>{" "}
                      {
                        projectTypeOptions.find(
                          (item) => item.code === project?.segmentCode
                        )?.[
                          { us: "enName", vn: "vnName", kr: "krName" }[
                            user?.language
                          ]
                        ]
                      }
                    </p>
                    <p>
                      <b>
                        {languageMap?.[
                          "event.dashboard.myProject.createdDate"
                        ] ?? "Created Date"}
                        :
                      </b>{" "}
                      {formatDate(project?.createdAt)}
                    </p>
                    <div className="mt-[10px] flex flex-wrap justify-between gap-2">
                      <Button
                        className="p-[4px] flex-1 p-[2px]"
                        type="primary"
                        onClick={() => handleCalendarClick(project)}
                      >
                        {languageMap?.["event.dashboard.myProject.calendar"] ??
                          "Calendar"}
                      </Button>
                      <Button
                        className="p-[4px] flex-1 p-[2px]"
                        type="primary"
                        onClick={() =>
                          handleProjectNameClickProjectDetail(project)
                        }
                      >
                        {languageMap?.[
                          "project.management.config.management"
                        ] ?? "Project Management"}
                      </Button>
                      <Button
                        className="p-[4px] flex-1 p-[2px]"
                        type="primary"
                        onClick={() => handleTimelineClick(project)}
                      >
                        {languageMap?.["project.management.config.viewDetails"] ?? "View Details"}
                      </Button>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}

            <div className="flex justify-center mt-4 pagination shadow-md">
              <Pagination
                current={pagination?.current}
                pageSize={pagination?.pageSize}
                total={pagination?.total}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          </Row>
        ) : (
          <Table
            className="custom-table-scroll"
            columns={columns}
            dataSource={projectList}
            pagination={pagination}
            loading={loading}
            size={"middle"}
            onChange={handleTableChange}
            rowKey={(record) => record.key}
            // onRow={(record, index) => ({
            //   onClick: (e) => handleTimelineClick(record),
            // })}
            scroll={{
              x: 1000,
              // y: 300,
            }}
            // style={{ maxHeight: '400px' }}
            rowClassName="dashboard-row"
          />
        )}
      </div>
    </>
  );
};

export { ProjectList };
